import { UserDetailsResponseInterface } from "../models/UserDetails";
import { UserListResponse, UserFilters } from "../models/types";
import { defaultFetch, fetchWithAuth } from "../utils/auth";
import { Profile } from "../pages/Profile/Profile";
import { IDependant } from "../models/Profile";

export const CURRENT_USER = "CurrentUser";

const translateResponse = (data: any) => {
  if (data.error) {
    throw new Error(data.error);
  }

  return {
    data: data?.users,
    pageInformation: data?.pageInformation,
    filters: data?.filtersInformation?.filters,
  };
};

const createFiltersQueries = (userFilters: UserFilters | null) => {
  if (!userFilters) {
    return "";
  }

  const queryParams: string[] = [];

  for (const [key, value] of Object.entries(userFilters)) {
    if (value !== null) {
      if (Array.isArray(value)) {
        for (const val of value) {
          queryParams.push(
            `${encodeURIComponent(key)}=${encodeURIComponent(val.toString())}`
          );
        }
      } else {
        queryParams.push(
          `${encodeURIComponent(key)}=${encodeURIComponent(value.toString())}`
        );
      }
    }
  }

  return queryParams.join("&");
};

export const readUsers = async (
  pageSize = 5,
  pageIndex = 0,
  filters: UserFilters | null
): Promise<UserListResponse> => {
  try {
    const filterQueries = createFiltersQueries(filters);

    const fetchUrl = `/api/backend/v1/users?${
      filterQueries ? filterQueries + "&" : ""
    }pageSize=${pageSize}&pageIndex=${pageIndex}`;

    const res = await fetchWithAuth(fetchUrl, {
      headers: {
        "Content-Type": "application/json",
      },
    });

    if (res.status >= 400 && res.status < 600) {
      throw new Error("Server not returned a valid response");
    }
    const remoteData = await res.json();
    const data = translateResponse(remoteData);

    return new UserListResponse(data);
  } catch (error) {
    let message;
    if (error instanceof Error) {
      message = error.message;
    } else {
      message = "Unable to load shows";
    }

    return new UserListResponse({ error: message });
  }
};

export const updateUserGender = async (gender: string): Promise<boolean> => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/gender`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({ gender }),
    });

    if (res.status >= 400 && res.status < 600) {
      return false;
    }

    return true;
  } catch (error) {
    return false;
  }
};

export const updateUser = async (user: any) => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify(user),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      return false;
    }

    return true;
  } catch (error) {
    return error;
  }
};

export const sendPhoneNumberVerificationCode = async (user: any, tokenOrbookingInterestId: string | number): Promise<any> => {
  try {
    const fetchUrl = `/api/backend/v1/users/send-phone-number-verification-code/${tokenOrbookingInterestId}`;
    let res;

    if (typeof tokenOrbookingInterestId === 'string') {
      res = await defaultFetch(fetchUrl, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(user),
      });
    } else {
      res = await fetchWithAuth(fetchUrl, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify(user),
      });
    }

    if (res.status >= 400 && res.status < 600) {
      var response = await res.json();
      return response
    }
    return res;
  } catch (error) {
    return error;
  }
};

export const confirmAndUpdateUserPhoneNumber = async (phoneNumber: string, phoneNumberVerificationCode: string, tokenOrbookingInterestId: string | number): Promise<any> => {
  try {
    const fetchUrl = `/api/backend/v1/users/confirm-and-update-phone-number/${tokenOrbookingInterestId}`;
    let res;

    if (typeof tokenOrbookingInterestId === 'string') {
      res = await defaultFetch(fetchUrl, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({phoneNumber, phoneNumberVerificationCode}),
      });
    } else {
      res = await fetchWithAuth(fetchUrl, {
        headers: {
          "Content-Type": "application/json",
        },
        method: "PUT",
        body: JSON.stringify({phoneNumber, phoneNumberVerificationCode}),
      });
    }

    if (res.status >= 400 && res.status < 600) {
      var response = await res.json();
      return response
    }
    return res;
  } catch (error) {
    return {error};
  }
};

export const updateStubUser = async (user: any) => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/stub-user`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify(user),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      return false;
    }

    return true;
  } catch (error) {
    return error;
  }
};

export const getUserDetails =  async (id: string) => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/${id}`);

    if (res.status >= 400 && res.status < 600) {
      throw new Error("Server not returned a valid response");
    }

    const userDetails: UserDetailsResponseInterface = await res.json();
    return userDetails.user;
  } catch (error) {
    return error;
  }
};

export const getProfile = async (): Promise<Profile> => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/profile`);

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      throw { loginStatus: "logged out" };
    }
    const profile = await res.json();
    return profile;
  } catch (error) {
    throw error;
  }
};

export const changeMailingList = async (value: string): Promise<boolean> => {
  try {
    const res = await fetchWithAuth(
      `/api/backend/v1/users/mailing-list?emailPreference=${value}`,
      {
        method: "POST",
      }
    );

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      return false;
    }

    return true;
  } catch (error) {
    throw error;
  }
};

export const setIsBlacklisted = async (userId: string, isBlacklisted: boolean): Promise<boolean> => {
  try {
    const res = await fetchWithAuth(
      `/api/backend/v1/users/set-blacklisted`,
      {
        headers: {
          "Content-Type": "application/json",
        },
        method: "POST",
        body: JSON.stringify({"UserId": userId, "IsBlacklisted": isBlacklisted}),
      }
    );

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      return false;
    }

    return true;
  } catch (error) {
    throw error;
  }
};

export const getDependants = async (): Promise<IDependant[]> => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/dependent-profiles`);

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      throw { loginStatus: "logged out" };
    }
    const dependants = await res.json();
    return dependants;
  } catch (error) {
    throw error;
  }
};

export const addDependant = async (dependant: IDependant): Promise<boolean> => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/dependent-profiles`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify(dependant),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      throw { loginStatus: "logged out" };
    }
    const { success } = await res.json();
    return success;
  } catch (error) {
    throw error;
  }
};

export const updateDependant = async (
  dependant: IDependant
): Promise<boolean> => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/dependent-profiles`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify(dependant),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      throw { loginStatus: "logged out" };
    }
    return true;
  } catch (error) {
    throw error;
  }
};

export const deleteDependant = async (
  dependantId: string
): Promise<boolean> => {
  try {
    const res = await fetchWithAuth(
      `/api/backend/v1/users/dependent-profiles?dependentProfileId=${dependantId}`,
      {
        method: "DELETE",
      }
    );

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      throw { loginStatus: "logged out" };
    }
    return true;
  } catch (error) {
    throw error;
  }
};

export const sendPhoneNumberVerificationCodeWithAuth = async (phoneNumber: string): Promise<boolean | any> => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/send-phone-number-verification-code`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({phoneNumber}),
    })

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      const errorBody = await res.json();

      if (errorBody && errorBody.errors && errorBody.errors.message) {
        return errorBody.errors.message;
      } else {
        return ["An error occurred while processing your request."];
      }
    }

    return true;
  } catch (error) {
    throw error;
  }
};

export const confirmAndUpdatePhoneNumberViaCode = async (phoneNumber: string, phoneNumberVerificationCode: string) => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/confirm-and-update-phone-number`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({"phoneNumber": phoneNumber, "phoneNumberVerificationCode": phoneNumberVerificationCode}),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      const errorBody = await res.json();

      if (errorBody && errorBody.errors && errorBody.errors.message) {
        return errorBody.errors.message;
      } else {
        return ["The verification code is not correct or has expired."];
      }
    }

    return true;
  } catch (error) {
    return error;
  }
};

export const sendEmailVerificationCode = async (email: string): Promise<boolean | any> => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/send-email-verification-code`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({email}),
    })


    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      const errorBody = await res.json();

      if (errorBody && errorBody.errors && errorBody.errors.message) {
        return errorBody.errors.message;
      } else {
        return ["An error occurred while processing your request."];
      }
    }

    return true;
  } catch (error) {
    return error;
  }
};

export const confirmAndUpdateEmailViaCode = async (email: string, emailVerificationCode: string) => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/confirm-and-update-email`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({"email": email, "emailVerificationCode": emailVerificationCode}),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      const errorBody = await res.json();

      if (errorBody && errorBody.errors && errorBody.errors.message) {
        return errorBody.errors.message;
      } else {
        return ["The verification code is not correct or has expired."];
      }
    }

    return true;
  } catch (error) {
    return error;
  }
};

export const adminUpdateFirstName = async (userId: string, firstName: string) => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/admin-update-first-name`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({"UserId": userId, "FirstName": firstName}),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      const errorBody = await res.json();

      if (errorBody && errorBody.errors && errorBody.errors.message) {
        return errorBody.errors.message;
      } else {
        return ["Error while updating first name."];
      }
    }

    return true;
  } catch (error) {
    return error;
  }
};

export const adminUpdateLastName = async (userId: string, lastName: string) => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/admin-update-last-name`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({"UserId": userId, "LastName": lastName}),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      const errorBody = await res.json();

      if (errorBody && errorBody.errors && errorBody.errors.message) {
        return errorBody.errors.message;
      } else {
        return ["Error while updating last name."];
      }
    }

    return true;
  } catch (error) {
    return error;
  }
};

export const adminUpdateEmail = async (userId: string, email: string) => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/admin-update-email`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({"UserId": userId, "Email": email}),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      const errorBody = await res.json();

      if (errorBody && errorBody.errors && errorBody.errors.message) {
        return errorBody.errors.message;
      } else {
        return ["Error while updating email."];
      }
    }

    return true;
  } catch (error) {
    return error;
  }
};

export const adminUpdatePhoneNumber = async (userId: string, phoneNumber: string) => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/admin-update-phone-number`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({"UserId": userId, "PhoneNumber": phoneNumber}),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      const errorBody = await res.json();

      if (errorBody && errorBody.errors && errorBody.errors.message) {
        return errorBody.errors.message;
      } else {
        return ["Error while updating phone number."];
      }
    }

    return true;
  } catch (error) {
    return error;
  }
};

export const adminUpdateBirthDate = async (userId: string, dateOfBirth : string) => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/admin-update-birth-date`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "PUT",
      body: JSON.stringify({"UserId": userId, "DateOfBirth": dateOfBirth}),
    });

    if ((res.status >= 400 && res.status < 600) || res.status == 500) {
      const errorBody = await res.json();

      if (errorBody && errorBody.errors && errorBody.errors.message) {
        return errorBody.errors.message;
      } else {
        return ["Error while updating birth date."];
      }
    }

    return true;
  } catch (error) {
    return error;
  }
};
/**
 * Of the addresses given, the ones with no account able to log in — no user at all, or a stub
 * a previous lead booker created who never finished signing up.
 *
 * Used by the booking form to warn before an application is submitted. A failure here is not
 * worth stopping an application over, so the caller gets an empty list and the booking goes
 * through unwarned rather than blocked.
 */
export const getUnregisteredEmails = async (emails: string[]): Promise<string[]> => {
  try {
    const res = await fetchWithAuth(`/api/backend/v1/users/unregistered-emails`, {
      headers: {
        "Content-Type": "application/json",
      },
      method: "POST",
      body: JSON.stringify({ emails }),
    });

    if (res.status >= 400) {
      return [];
    }

    return await res.json();
  } catch (error) {
    return [];
  }
};
